import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BarChart3, Printer, ShieldCheck, Tag, TrendingUp, Target, CalendarCheck, Megaphone, Loader2 } from "lucide-react";

const money = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n || 0);

export default function RapportsModule() {
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/accueil-pilotage/rapports"); setR(data); }
    catch { toast.error("Chargement échoué."); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !r) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-400"><Loader2 className="animate-spin mr-2" /> Compilation du rapport…</div>;

  const Bar = ({ pct, tone = "bg-emerald-500" }) => (
    <div className="h-2 rounded-full bg-black/40 overflow-hidden mt-2"><div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
  );
  const Card = ({ icon: Icon, title, children, tone = "text-[#C9A961]" }) => (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 print-card">
      <div className="flex items-center gap-2 mb-3"><Icon className={`w-5 h-5 ${tone}`} /><h2 className="text-white font-semibold text-sm">{title}</h2></div>
      {children}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="rapports-page">
      <style>{`@media print{.no-print{display:none!important}body{background:#fff}}`}</style>
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[#C9A961] text-xs uppercase tracking-widest">Accueil & Pilotage</div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2"><BarChart3 className="w-7 h-7 text-[#C9A961]" /> Rapports & optimisation</h1>
          <p className="text-zinc-400 text-sm mt-1">Bilan de performance du dojo · {new Date(r.generated_at).toLocaleDateString("fr-CA", { year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
        <button onClick={() => window.print()} data-testid="rapport-print" className="no-print bg-[#C9A961] hover:bg-white text-black font-semibold px-4 py-2 rounded-xl text-sm flex items-center gap-2"><Printer className="w-4 h-4" /> Imprimer / PDF</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rapport-cards">
        <Card icon={ShieldCheck} title="Conformité des membres">
          <div className="text-3xl font-extrabold text-white">{r.conformite.taux}%</div>
          <div className="text-xs text-zinc-400">{r.conformite.conformes}/{r.conformite.total} dossiers conformes · {r.conformite.a_corriger} à corriger</div>
          <Bar pct={r.conformite.taux} />
        </Card>
        <Card icon={Tag} title="Tarifs & forfaits" tone="text-orange-400">
          <div className="text-3xl font-extrabold text-white">{r.tarifs.incoherences}</div>
          <div className="text-xs text-zinc-400">incohérences · {r.tarifs.en_retard} paiements en retard · {r.tarifs.tarif_manquant} tarifs non confirmés</div>
        </Card>
        <Card icon={TrendingUp} title="Rétention (relances 48 h)" tone="text-emerald-400">
          <div className="text-3xl font-extrabold text-white">{r.retention.taux}%</div>
          <div className="text-xs text-zinc-400">{r.retention.conversions}/{r.retention.relances} essais relancés → inscriptions</div>
          <Bar pct={r.retention.taux} />
        </Card>
        <Card icon={Target} title="Financement" tone="text-emerald-400">
          <div className="text-3xl font-extrabold text-white">{money(r.financement.recolte)}</div>
          <div className="text-xs text-zinc-400">sur {money(r.financement.objectif)} · {r.financement.taux}% atteint</div>
          <Bar pct={r.financement.taux} />
        </Card>
        <Card icon={CalendarCheck} title="Assiduité Petits Samouraïs" tone="text-sky-400">
          <div className="text-3xl font-extrabold text-white">{r.presences.assiduite_moyenne}%</div>
          <div className="text-xs text-zinc-400">assiduité moyenne · {r.presences.enfants_suivis} enfant(s) suivi(s)</div>
          <Bar pct={r.presences.assiduite_moyenne} tone="bg-sky-500" />
        </Card>
        <Card icon={Megaphone} title="Communications & événements" tone="text-violet-400">
          <div className="text-3xl font-extrabold text-white">{r.communications.total}</div>
          <div className="text-xs text-zinc-400">{r.communications.en_attente} en attente d'approbation · {r.evenements.a_venir} événement(s) à venir</div>
        </Card>
      </div>

      <p className="text-[11px] text-zinc-600 no-print">Astuce : imprime ce rapport en PDF pour le présenter à ton équipe lors de la rencontre de coachs.</p>
    </div>
  );
}
